// Counterpart of webapp/src/store/sidebar.ts

import {create} from 'zustand'
import {CategoryBoards} from '../types/category'
import client from '../api/client'
import {Constants} from '../common/constants'
import {Utils} from '../common/utils'

type SidebarState = {
    categories: CategoryBoards[]
    loading: boolean
    isHidden: boolean
    size: number

    loadCategories: (teamId: string) => Promise<CategoryBoards[]>
    toggleCategoryCollapsed: (teamId: string, categoryId: string) => Promise<void>
    hideBoardFromCategory: (teamId: string, categoryId: string, boardId: string) => Promise<void>
    toggleSidebar: (force?: boolean) => void
    setSize: (size: number) => void
}

function readPersisted(): {isHidden: boolean, size: number} {
    const raw = Utils.getItem(Constants.localStorageKeys.sidebarSize)
    if (raw) {
        try {
            const parsed = JSON.parse(raw) as {hidden?: boolean, size?: number}
            return {isHidden: Boolean(parsed.hidden), size: Number(parsed.size) || 240}
        } catch {
            // fall through to defaults
        }
    }
    return {isHidden: false, size: 240}
}

const persisted = typeof window === 'undefined' ? {isHidden: false, size: 240} : readPersisted()

function persistLayout(state: SidebarState): void {
    Utils.setItem(Constants.localStorageKeys.sidebarSize, JSON.stringify({hidden: state.isHidden, size: state.size}))
}

export const useSidebar = create<SidebarState>((set, get) => ({
    categories: [],
    loading: false,
    isHidden: persisted.isHidden,
    size: persisted.size,

    async loadCategories(teamId) {
        set({loading: true})
        const categories = await client.getSidebarCategories(teamId)
        set({categories, loading: false})
        return categories
    },

    async toggleCategoryCollapsed(teamId, categoryId) {
        const category = get().categories.find((c) => c.id === categoryId)
        if (!category) {
            return
        }
        const next = {...category, collapsed: !category.collapsed, updateAt: Date.now()}
        set((state) => ({categories: state.categories.map((c) => (c.id === categoryId ? next : c))}))
        await client.patchSidebarCategory(teamId, next)
    },

    async hideBoardFromCategory(teamId, categoryId, boardId) {
        const category = get().categories.find((c) => c.id === categoryId)
        if (!category) {
            return
        }
        const metadata = category.boardMetadata.map((m) => (m.boardID === boardId ? {...m, hidden: true} : m))
        const next = {...category, boardMetadata: metadata, updateAt: Date.now()}
        set((state) => ({categories: state.categories.map((c) => (c.id === categoryId ? next : c))}))
        await client.patchSidebarCategory(teamId, next)
    },

    toggleSidebar(force) {
        set((state) => {
            const isHidden = force === undefined ? !state.isHidden : force
            const next = {...state, isHidden}
            persistLayout(next)
            return {isHidden}
        })
    },

    setSize(size) {
        set((state) => {
            const next = {...state, size}
            persistLayout(next)
            return {size}
        })
    },
}))

/** Boards of a category, minus hidden ones, ordered by the category metadata. */
export function selectCategoryBoardIds(categories: CategoryBoards[], categoryId: string): string[] {
    const category = categories.find((c) => c.id === categoryId)
    if (!category) {
        return []
    }
    return category.boardMetadata.filter((m) => !m.hidden).map((m) => m.boardID)
}

/** Boards that no custom category claims — upstream lists them under "Boards". */
export function selectUncategorizedBoardIds(categories: CategoryBoards[], allBoardIds: string[]): string[] {
    const claimed = new Set(categories.flatMap((c) => c.boardMetadata.map((m) => m.boardID)))
    return allBoardIds.filter((id) => !claimed.has(id))
}
